import React, { useState } from 'react';
import axiosInstance from '../../api/axiosInstance';
import toast from 'react-hot-toast';
import { Input, Select, Button } from '../../components/ui';
import { FormField } from '../../components/ui/Form';
import { getFieldIcon } from '../../utils/formIcons';
// Heroicons
import { CheckCircleIcon, TruckIcon, BuildingOffice2Icon, ExclamationTriangleIcon, ArrowPathIcon, ArchiveBoxIcon, ArrowRightOnRectangleIcon, XCircleIcon, ClipboardDocumentCheckIcon, MagnifyingGlassIcon, DocumentArrowDownIcon, MapPinIcon, CalendarIcon, CurrencyDollarIcon, CubeIcon } from '@heroicons/react/24/outline';

const TRANSPORTADORAS = [
  { value: 'envia', label: 'Envia' },
  // Agrega más transportadoras aquí si es necesario
];

// Definición de los pasos principales del envío
const STEPS = [
  { code: 1, label: 'Recolección', icon: <ArchiveBoxIcon className="h-10 w-10" /> },
  { code: 2, label: 'Despacho', icon: <ClipboardDocumentCheckIcon className="h-10 w-10" /> },
  { code: 3, label: 'En tránsito', icon: <TruckIcon className="h-10 w-10" /> },
  { code: 4, label: 'Oficina destino', icon: <BuildingOffice2Icon className="h-10 w-10" /> },
  { code: 5, label: 'En reparto', icon: <ArrowPathIcon className="h-10 w-10" /> },
  { code: 13, label: 'Entregado', icon: <CheckCircleIcon className="h-10 w-10" /> },
];

// Estados de error o novedad
const ERROR_CODES = [6, 7, 8, 9, 10];
const ERROR_LABELS = {
  6: 'Novedad',
  7: 'Coordinar Entrega',
  8: 'No Entregado',
  9: 'Devuelto',
  10: 'Cancelado',
};
const ERROR_ICONS = {
  6: <ExclamationTriangleIcon className="h-10 w-10" />,
  7: <ExclamationTriangleIcon className="h-10 w-10" />,
  8: <XCircleIcon className="h-10 w-10" />,
  9: <ArrowRightOnRectangleIcon className="h-10 w-10" />,
  10: <XCircleIcon className="h-10 w-10" />,
};

function ProgressSteps({ currentCode }: { currentCode: number }) {
  // Si es un estado de error, lo mostramos como un paso especial
  if (ERROR_CODES.includes(currentCode)) {
    return (
      <div className="flex items-center justify-center my-12">
        <div className="flex flex-col items-center">
          <div className="rounded-full p-5 bg-red-600 text-white">
            {ERROR_ICONS[currentCode as 6 | 7 | 8 | 9 | 10]}
          </div>
          <span className="mt-4 text-base font-semibold text-red-600 text-center">{ERROR_LABELS[currentCode as 6 | 7 | 8 | 9 | 10]}</span>
        </div>
      </div>
    );
  }
  // Buscar el índice del paso actual
  const currentIndex = STEPS.findIndex(s => s.code === currentCode);
  return (
    <div className="flex items-center justify-between my-12 w-full max-w-5xl mx-auto">
      {STEPS.map((step, idx) => {
        const isActive = idx === currentIndex;
        const isCompleted = idx < currentIndex;
        return (
          <React.Fragment key={step.code}>
            <div className="flex flex-col items-center flex-1 min-w-[90px]">
              <div className="flex flex-col items-center w-full">
                <div className={`rounded-full p-4 transition-colors duration-200 z-10 ${isActive ? 'bg-blue-600 text-white' : isCompleted ? 'bg-blue-200 text-blue-600' : 'bg-gray-200 text-gray-400'}`}>
                  {step.icon}
                </div>
                <span className={`mt-4 text-base font-semibold text-center ${isActive ? 'text-blue-600' : isCompleted ? 'text-blue-400' : 'text-gray-400'}`}>{step.label}</span>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`flex-1 h-2 ${isCompleted ? 'bg-blue-400' : 'bg-gray-200'}`} style={{marginTop: '-32px', width: '100%'}}></div>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

const SeguimientoEnvios: React.FC = () => {
  const [transportadora, setTransportadora] = useState('');
  const [guia, setGuia] = useState('');
  const [resultado, setResultado] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleBuscar = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResultado(null);
    try {
      const response = await axiosInstance.get(
        `/guias/seguimiento/${transportadora}/${guia}`
      );
      if (response.data.success && response.data.data) {
        setResultado(response.data.data);
        toast.success('Información de seguimiento encontrada', { icon: '✅', position: 'top-center' });
      } else {
        const errorMsg = 'No se encontró información para la guía ingresada.';
        setError(errorMsg);
        toast.error(errorMsg, { icon: '⚠️', position: 'top-center' });
      }
    } catch (err) {
      const errorMsg = 'No se encontró información para la guía ingresada o hubo un error en la consulta.';
      setError(errorMsg);
      toast.error(errorMsg, { icon: '❌', position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-center mb-2">
            <TruckIcon className="h-8 w-8 text-custom-blue mr-3" />
            <h2>Seguimiento de Envíos</h2>
          </div>
          <p className="text-center text-gray-600 text-sm">Consulta el estado de tus envíos en tiempo real</p>
        </div>

        {/* Formulario de búsqueda */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <form onSubmit={handleBuscar} className="flex flex-col md:flex-row gap-4 justify-center items-end">
            <FormField label="Transportadora" icon={getFieldIcon('transportadora')} className="flex-1 min-w-[200px]">
              <Select
                value={transportadora}
                onChange={e => setTransportadora(e.target.value)}
                required
                options={TRANSPORTADORAS}
                placeholder="Seleccione transportadora"
                size="md"
              />
            </FormField>
            <FormField label="Número de guía" icon={getFieldIcon('guia')} className="flex-1 min-w-[200px]">
              <Input
                type="text"
                placeholder="Ingrese el número de guía"
                value={guia}
                onChange={e => setGuia(e.target.value)}
                required
                size="md"
                leftIcon={MagnifyingGlassIcon}
              />
            </FormField>
            <Button
              type="submit"
              variant="primary"
              disabled={loading || !transportadora || !guia}
              isLoading={loading}
              leftIcon={MagnifyingGlassIcon}
              size="md"
              className="md:w-auto w-full mb-2 p-2"
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
          </form>
        </div>

        {/* Resultado del seguimiento */}
        {resultado && (
          <div className="space-y-6 animate-fadeIn">
            {/* Progress Steps visual */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <ProgressSteps currentCode={parseInt(resultado.cod_estadog)} />
            </div>

            {/* Tarjetas de información */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Bloque 1: Estado y guía */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-md border border-blue-200 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-center justify-center mb-4">
                  <div className="bg-blue-600 rounded-full p-3">
                    <ClipboardDocumentCheckIcon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-blue-800 text-center mb-3">Estado del Envío</h3>
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-blue-900 mb-2">{resultado.estado}</p>
                  <div className="flex items-center justify-center gap-2 text-sm text-blue-700">
                    <span className="font-semibold">Guía:</span>
                    <span className="font-mono">{resultado.guia}</span>
                  </div>
                </div>
              </div>

              {/* Bloque 2: Datos principales */}
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-center mb-4 pb-3 border-b border-gray-200">
                  <TruckIcon className="h-6 w-6 text-gray-700 mr-2" />
                  <h3 className="text-lg font-bold text-gray-800">Datos del Envío</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start">
                    <span className="font-semibold text-gray-700 min-w-[100px]">Remitente:</span>
                    <span className="text-gray-600 flex-1">{resultado.nombre_remitente}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-semibold text-gray-700 min-w-[100px]">Destinatario:</span>
                    <span className="text-gray-600 flex-1">{resultado.nombre_destinatario}</span>
                  </div>
                  <div className="flex items-start">
                    <MapPinIcon className="h-4 w-4 text-gray-500 mr-1 mt-0.5" />
                    <span className="font-semibold text-gray-700 min-w-[90px]">Origen:</span>
                    <span className="text-gray-600 flex-1">{resultado.ciudad_origen}</span>
                  </div>
                  <div className="flex items-start">
                    <MapPinIcon className="h-4 w-4 text-gray-500 mr-1 mt-0.5" />
                    <span className="font-semibold text-gray-700 min-w-[90px]">Destino:</span>
                    <span className="text-gray-600 flex-1">{resultado.ciudad_destino}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-semibold text-gray-700 min-w-[100px]">Dirección:</span>
                    <span className="text-gray-600 flex-1">{resultado.direccion_destinatario}</span>
                  </div>
                  <div className="flex items-center gap-4 pt-2 border-t border-gray-100">
                    <div className="flex items-center">
                      <CubeIcon className="h-4 w-4 text-gray-500 mr-1" />
                      <span className="font-semibold text-gray-700 mr-2">Unidades:</span>
                      <span className="text-gray-600">{resultado.unidades}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-semibold text-gray-700 mr-2">Peso:</span>
                      <span className="text-gray-600">{resultado.peso_real} kg</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bloque 3: Costos y fechas */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-center mb-4 pb-3 border-b border-gray-300">
                  <BuildingOffice2Icon className="h-6 w-6 text-gray-700 mr-2" />
                  <h3 className="text-lg font-bold text-gray-800">Costos y Fechas</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                    <span className="font-semibold text-gray-700 flex items-center">
                      <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                      Total:
                    </span>
                    <span className="text-lg font-bold text-green-600">${parseFloat(resultado.total).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-700">Declarado:</span>
                    <span className="text-gray-600">${parseFloat(resultado.declarado).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-700">Flete:</span>
                    <span className="text-gray-600">${parseFloat(resultado.flete).toLocaleString()}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-300 space-y-2">
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 text-gray-500 mr-2" />
                      <div className="flex-1">
                        <span className="font-semibold text-gray-700 text-xs block">Producción:</span>
                        <span className="text-gray-600 text-sm">{resultado.fecha_produccion}</span>
                      </div>
                    </div>
                    {resultado.fecha_entrega && (
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 text-gray-500 mr-2" />
                        <div className="flex-1">
                          <span className="font-semibold text-gray-700 text-xs block">Entrega:</span>
                          <span className="text-gray-600 text-sm">
                            {resultado.fecha_entrega} {resultado.hora && `- ${resultado.hora}`}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Botón PDF centrado */}
            {resultado.imagen && (
              <div className="flex justify-center">
                <Button
                  variant="primary"
                  onClick={() => window.open(resultado.imagen, '_blank', 'noopener,noreferrer')}
                  leftIcon={DocumentArrowDownIcon}
                  size="lg"
                  className="shadow-md hover:shadow-lg transition-shadow"
                >
                  Ver PDF de la Guía
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Estado vacío inicial */}
        {!resultado && !loading && !error && (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <TruckIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Consulta el estado de tu envío</h3>
            <p className="text-gray-500">Ingresa la transportadora y el número de guía para comenzar</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeguimientoEnvios; 